<template>
  <!-- The picker's menu must escape the body only while nothing sits under the picker; once the steps show, the body scrolls -->
  <Modal :content-class="receiver ? undefined : 'team-picker-dialog'" ref="modalRef" width="30rem">
    <template #header>Reassign manager</template>

    <template #body>
      <View direction="column" gap="md" class="form">
        <Text variant="label-medium">New manager</Text>

        <TeamMemberPicker v-model="investorId" :members="members" :exclude-ids="[manager?.investorId]" :hints="hints" />

        <Hint v-if="!receiver" icon="info-circle">
          Pick who takes over this {{ noun }}.
          <template v-if="subject.kind === 'property'">
            From then on Tallo sends its requests, questions and reminders to them, and its showings land on their
            calendar.
          </template>
        </Hint>

        <template v-if="receiver">
          <View direction="column" gap="sm">
            <Text variant="h4">What happens after you reassign:</Text>

            <Card variant="outline" size="medium">
              <View direction="column" gap="md">
                <View v-for="step in steps" :key="step.title" gap="md">
                  <IconTile size="small" color="orange" :icon="step.icon" />

                  <View direction="column" gap="xs">
                    <Text variant="h5">{{ step.title }}</Text>
                    <Text variant="body-medium" color="secondary">{{ step.description }}</Text>
                  </View>
                </View>
              </View>
            </Card>
          </View>

          <View v-if="manager" gap="sm" align="center">
            <Checkbox id="hand-off-remove-previous" v-model="removePrevious" />
            <Text variant="body-medium" as="label" for="hand-off-remove-previous">{{ removeLabel }}</Text>
          </View>
        </template>
      </View>
    </template>

    <template #footer>
      <Button variant="ghost" @click="close">Cancel</Button>
      <Button :loading="isLoading" :disabled="!receiver" @click="handOff">Reassign</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { Button, Card, Checkbox, Hint, IconTile, Modal, Text, View, useAlertsStore } from '@tallo/design-system';
import {
  CompanyTeamMemberDto,
  TeamMemberDto,
  TeamMemberPicker,
  teamMemberName as nameOf,
} from '@tallo/investor/company';
import { escapeHtml } from '@tallo/utility';

import { TeamSubject } from '../../dtos/property-team.dto.interface';
import { useHandOffProperty } from '../../mutations/property-team.mutation';
import { subjectNoun } from './team-roles';

interface Step {
  icon: InstanceType<typeof IconTile>['$props']['icon'];
  title: string;
  description: string;
}

const props = withDefaults(
  defineProps<{
    subject: TeamSubject;
    members: CompanyTeamMemberDto[];
    manager?: TeamMemberDto | null;
    managerIsYou?: boolean;
    /** What each seat already is on this unit, shown beside them in the picker */
    hints?: Record<string, string>;
    unitName?: string;
  }>(),
  { hints: () => ({}) },
);

const alertsStore = useAlertsStore();
const modalRef = ref<InstanceType<typeof Modal>>();
const investorId = ref<string>();
// Off by default: handing a unit on should not quietly take it away from the person who had it
const removePrevious = ref(false);
const { mutateAsync, isLoading } = useHandOffProperty();

const receiver = computed(() => props.members.find((member) => member.investorId === investorId.value));
const noun = computed(() => subjectNoun(props.subject));
const managerName = computed(() => (props.manager ? nameOf(props.manager) : ''));

/** What the hand-off changes, as the accept dialogs list it: routing, the previous manager, and who is told */
const steps = computed<Step[]>(() => {
  const target = receiver.value;

  if (!target) {
    return [];
  }

  const name = nameOf(target);
  // Calendar events made before the hand-off stay where they are; only new showings reach the new calendar
  const bookedStay = !props.manager
    ? ''
    : props.managerIsYou
      ? ' Showings already booked stay on your calendar.'
      : ` Showings already booked stay on ${managerName.value}'s calendar.`;

  const routing: Step = {
    icon: 'bell-01',
    title: `Tallo routes this unit to ${name}`,
    description: `New showing requests, renter questions and reminders go to ${name}, and new showings land on their calendar.${bookedStay}`,
  };
  // A building without units has no showings or renter questions to route
  const routes = props.subject.kind === 'property' ? [routing] : [];
  const notTold: Step = {
    icon: 'message-chat-circle',
    title: `${name} isn't told`,
    description: "Tallo doesn't message teammates about role changes, so let them know.",
  };

  return [...routes, ...(props.manager ? [previousManagerStep()] : []), notTold];
});

function previousManagerStep(): Step {
  if (props.managerIsYou) {
    return removePrevious.value
      ? {
          icon: 'user-01',
          title: `You step away from this ${noun.value}`,
          description: `You stop working this ${noun.value}. Untick below to stay on as a collaborator.`,
        }
      : {
          icon: 'user-01',
          title: 'You stay on as a collaborator',
          description: `You keep working this ${noun.value}, without its notifications. Tick below to step away from it instead.`,
        };
  }

  return removePrevious.value
    ? {
        icon: 'user-01',
        title: `${managerName.value} leaves this ${noun.value}`,
        description: `They stop working this ${noun.value}. Untick below to keep them on as a collaborator.`,
      }
    : {
        icon: 'user-01',
        title: `${managerName.value} stays on as a collaborator`,
        description: `They keep working this ${noun.value}, without its notifications. Tick below to remove them instead.`,
      };
}

const removeLabel = computed(() =>
  props.managerIsYou
    ? `Also remove me from this ${noun.value}`
    : `Also remove ${managerName.value} from this ${noun.value}`,
);

/** Opens with nobody picked, or with the given seat - "Make manager" on a collaborator's row */
function open(preselectedInvestorId?: string) {
  investorId.value = preselectedInvestorId;
  removePrevious.value = false;
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

async function handOff() {
  const target = receiver.value;

  if (!target) {
    return;
  }

  const name = nameOf(target);

  try {
    await mutateAsync({
      subject: props.subject,
      investorId: target.investorId,
      // With no manager there is nobody to keep, and asking to keep them would fail the hand-off
      keepPreviousAsCollaborator: Boolean(props.manager) && !removePrevious.value,
    });
  } catch {
    // The mutation already showed the server's message; the dialog stays open for another try
    return;
  }

  close();
  alertsStore.success(
    `<b>${escapeHtml(name)}</b> now manages ${props.unitName ? escapeHtml(props.unitName) : `this ${noun.value}`}`,
  );
}

defineExpose({ open });
</script>

<style scoped lang="scss">
.form {
  width: 100%;
}
</style>
